import React, {useState} from 'react'
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import {
  leisureInterestOptions,
  yesNoOptions,
  responseOptions,
  motorResponseOptions,
  verbalResponseOptions,
  affectOptions,
  hearingVisionGagSpeechOptions,
  initialStateForm2 as initialState,
} from './NURSES_ASSESSMENT_FORM_CONSTANTS'

function CheckboxGroup({label, options, value, onChange}) {
  return (
    <FormControl component='fieldset' sx={{mb: 2}}>
      <Typography variant='subtitle2'>{label}</Typography>
      <FormGroup row>
        {options.map((opt, i) => (
          <FormControlLabel
            key={opt.value}
            control={
              <Checkbox
                checked={value.includes(opt.value)}
                onChange={(e) => onChange(opt.value, e.target.checked)}
              />
            }
            label={opt.label}
            sx={{mr: 1, width: 300}}
          />
        ))}
      </FormGroup>
    </FormControl>
  )
}

const NursesAssessmentForm2 = ({data = {}, setFormValues, onSubmit, buttons}) => {
  const [formState, setFormState] = useState({...initialState, ...data})

  const handleChange = (name, value) => {
    setFormState((prev) => {
      const updated = {...prev, [name]: value}
      setFormValues && setFormValues(updated)
      return updated
    })
  }

  const handleCheckboxChange = (name, optionValue, checked) => {
    setFormState((prev) => {
      const arr = Array.isArray(prev[name]) ? [...prev[name]] : []
      if (checked) {
        if (!arr.includes(optionValue)) arr.push(optionValue)
      } else {
        const idx = arr.indexOf(optionValue)
        if (idx > -1) arr.splice(idx, 1)
      }
      const updated = {...prev, [name]: arr}
      setFormValues && setFormValues(updated)
      return updated
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit && onSubmit(formState)
  }

  return (
    <Box component='form' onSubmit={handleSubmit} sx={{mt: 2, mx: 'auto'}}>
      <Box>
        <Typography variant='h6' color='primary' sx={{fontSize: '16px', fontWeight: 'bold', mb: 2}}>
          Psychosocial
        </Typography>
        <CheckboxGroup
          label='Leisure Interests'
          options={leisureInterestOptions}
          value={formState.leisureInterest}
          onChange={(val, checked) => handleCheckboxChange('leisureInterest', val, checked)}
        />
        <TextField
          label='Social Activities'
          value={formState.socialActivities}
          onChange={(e) => handleChange('socialActivities', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={1}
        />
        <TextField
          label='Coping Skills'
          value={formState.copingSkills}
          onChange={(e) => handleChange('copingSkills', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />

        {/* Grouped select fields: abuseHistory, selfHarm, etohConsumption, drugAbuse, caffeineIntake, tobaccoUse, cessationEducation */}
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2}}>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>History of abuse and/or neglect?</InputLabel>
            <Select
              value={formState.abuseHistory}
              label='History of abuse and/or neglect?'
              onChange={(e) => handleChange('abuseHistory', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Thoughts of harming self or others?</InputLabel>
            <Select
              value={formState.selfHarm}
              label='Thoughts of harming self or others?'
              onChange={(e) => handleChange('selfHarm', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>ETOH Consumption?</InputLabel>
            <Select
              value={formState.etohConsumption}
              label='ETOH Consumption?'
              onChange={(e) => handleChange('etohConsumption', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Drug Abuse?</InputLabel>
            <Select
              value={formState.drugAbuse}
              label='Drug Abuse?'
              onChange={(e) => handleChange('drugAbuse', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Caffeine Intake?</InputLabel>
            <Select
              value={formState.caffeineIntake}
              label='Caffeine Intake?'
              onChange={(e) => handleChange('caffeineIntake', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Tobacco Use?</InputLabel>
            <Select
              value={formState.tobaccoUse}
              label='Tobacco Use?'
              onChange={(e) => handleChange('tobaccoUse', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>If still smoking, cessation education given</InputLabel>
            <Select
              value={formState.cessationEducation}
              label='If still smoking, cessation education given'
              onChange={(e) => handleChange('cessationEducation', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          label='Other psychosocial (describe)'
          value={formState.otherPsychosocial}
          onChange={(e) => handleChange('otherPsychosocial', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <Typography
          variant='subtitle1'
          sx={{mt: 3, mb: 1, fontSize: '16px', fontWeight: 'bold'}}
          color='primary'
        >
          Neurologic
        </Typography>
        {/* Grouped select fields: response, motorResponse, verbalResponse, affect */}
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2}}>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Response</InputLabel>
            <Select
              value={formState.response}
              label='Response'
              onChange={(e) => handleChange('response', e.target.value)}
              size='small'
            >
              {responseOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Motor Response</InputLabel>
            <Select
              value={formState.motorResponse}
              label='Motor Response'
              onChange={(e) => handleChange('motorResponse', e.target.value)}
              size='small'
            >
              {motorResponseOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Verbal Response</InputLabel>
            <Select
              value={formState.verbalResponse}
              label='Verbal Response'
              onChange={(e) => handleChange('verbalResponse', e.target.value)}
              size='small'
            >
              {verbalResponseOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 30%', minWidth: 180}}>
            <InputLabel>Affect</InputLabel>
            <Select
              value={formState.affect}
              label='Affect'
              onChange={(e) => handleChange('affect', e.target.value)}
              size='small'
            >
              {affectOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField
          label='Other details'
          value={formState.otherAffect}
          onChange={(e) => handleChange('otherAffect', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        {/* Grouped select fields: hearing/vision and gagReflex/speech */}
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2}}>
          <FormControl fullWidth size='small' sx={{flex: '1 1 45%', minWidth: 180}}>
            <InputLabel>Hearing</InputLabel>
            <Select
              value={formState.hearing}
              label='Hearing'
              onChange={(e) => handleChange('hearing', e.target.value)}
              size='small'
            >
              {hearingVisionGagSpeechOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 45%', minWidth: 180}}>
            <InputLabel>Vision</InputLabel>
            <Select
              value={formState.vision}
              label='Vision'
              onChange={(e) => handleChange('vision', e.target.value)}
              size='small'
            >
              {hearingVisionGagSpeechOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2}}>
          <FormControl fullWidth size='small' sx={{flex: '1 1 45%', minWidth: 180}}>
            <InputLabel>Gag Reflex</InputLabel>
            <Select
              value={formState.gagReflex}
              label='Gag Reflex'
              onChange={(e) => handleChange('gagReflex', e.target.value)}
              size='small'
            >
              {hearingVisionGagSpeechOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{flex: '1 1 45%', minWidth: 180}}>
            <InputLabel>Speech</InputLabel>
            <Select
              value={formState.speech}
              label='Speech'
              onChange={(e) => handleChange('speech', e.target.value)}
              size='small'
            >
              {hearingVisionGagSpeechOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {buttons}
      </Box>
    </Box>
  )
}

export default NursesAssessmentForm2
